import React from 'react';
import { ControlSection } from './control-section.js';
import { GenericSettingsSection } from './generic-settings-section.js';
import { vendorName } from '@studio/design-system/themes/vendor.js';

// Pre-defined sections
// import { Manufacturer } from '../manufacturer/index.js';
// import { Monitoring } from '../monitoring/index.js';
// import { Video } from '../video/index.js';
import {
  IrisConnectionSection,
  type IrisConnectionTab
} from '../iris-connection/iris-connection-section.js';

import type { SectionProps } from '../shared-types.js';

/**
 * Tabs rendered from a component of ours rather than derived from the device
 * registry. Declaration order does NOT decide where they render — the whole tab
 * strip is sorted through CANONICAL_TAB_ORDER by orderSettingsTabs — so the
 * Control entry sitting second here still renders after Audio. The same
 * function withholds Control on an embedded device and when the camera's
 * registry Controls group has nothing visible: that group is the tab's only
 * content (the platform control settings live on Iris Connection → Control), so
 * a fixed slot here would otherwise open on "No settings available".
 *
 * Each panelEnumValue must be a member of the Panel enum: Scaffold resolves a
 * clicked tab with toEnum(Panel, value), so a value outside the enum yields a
 * tab that renders but does nothing on click.
 */
export const PREDEFINED_SECTIONS = Object.freeze([
  {
    sectionGroup: `${vendorName()} Connection`,
    panelEnumValue: 'irisconn'
  },
  {
    sectionGroup: 'Control',
    panelEnumValue: 'cntrl'
  }
  // TODO? this was the old way of doing it...
  // {
  //   sectionGroup: 'Manufacturer',
  //   panelEnumValue: 'm'
  // },
  // {
  //   sectionGroup: 'Monitoring',
  //   panelEnumValue: 'mnt'
  // },
  // {
  //   sectionGroup: 'Video Settings',
  //   panelEnumValue: 'vs'
  // }
]);

// Generic section factory
const createGenericSection =
  (sectionGroupKey: string, previewTitle: string) => (props: SectionProps) => (
    <GenericSettingsSection
      {...props}
      sectionGroupKey={sectionGroupKey}
      previewTitle={previewTitle}
    />
  );

/** Configuration record for section components in the video settings panel */
export const SECTION_CONFIG: Record<
  string,
  { icon?: string; label: string; component: React.ComponentType<any> }
> = {
  // Pre-defined sections
  [vendorName() + ' Connection']: {
    label: `${vendorName()} Connection`,
    // `Scaffold` builds `passthru` with `agent` + `defaultTab` so the
    // deep-link target sub-tab opens pre-selected. Spreading `...props`
    // already forwards `defaultTab` when Scaffold includes it;
    // declaring the receiver as `SectionProps & { defaultTab?: ... }`
    // keeps the prop typed.
    //
    // Advanced-settings analytics moved into the domain tracker (merged
    // from main): `IrisConnectionSection` now owns its own
    // `settingsLifecycleTracker.trackAdvancedSettingsSectionOpened()`
    // call, so the old `analyticsCategory`/`analyticsEventName` passthru
    // props were dropped from its interface. Per `stay-in-scope`
    // (ai.knowledge/global) we forward only what the component still
    // accepts.
    component: (props: SectionProps & { defaultTab?: IrisConnectionTab }) => {
      return <IrisConnectionSection {...props} />;
    }
  },
  // Control tab: the registry 'Controls' group (see control-section.tsx). The
  // platform control settings are NOT here; they live on Iris Connection → Control.
  Control: {
    icon: 'joystick',
    label: 'Control',
    component: (props: SectionProps) => {
      return <ControlSection {...props} />;
    }
  },
  // Generic sections using GenericSettingsSection
  Features: {
    icon: 'ken-burns',
    label: 'Features',
    component: createGenericSection('Features', 'Features')
  },
  // Generic sections using DeviceSettingsControls
  Video: {
    icon: 'camera',
    label: 'Video',
    component: createGenericSection('Video', 'Video')
  },
  Audio: {
    icon: 'audio',
    label: 'Audio',
    component: createGenericSection('Audio', 'Audio')
  },
  System: {
    icon: 'group',
    label: 'System',
    component: createGenericSection('System', 'System')
  }
};
